import { isEqual, throttle } from 'lodash-es';
import type { Vector3Tuple } from 'three';
import type { TemporalState } from 'zundo';
import { temporal } from 'zundo';
import type { StoreApi } from 'zustand';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { TrackIDManager, trackIDManager } from '@/manager';
import { Unleash, uuid } from '@/package';
import useEditingLabelBoxStore from '@/store/editingLabelBoxStore';
import type { Box, BoxWithLabel } from '@/type';
import { eulerToQuaternion, quaternionToEuler } from '@/util';

import type { LabelingBoxStoreActions, LabelingBoxStoreState } from './type';
import { changeAllBoxProps, getBoxList, getCurrentPcdIndex } from './util';

const useLabelingBoxStore = createWithEqualityFn(
  temporal<LabelingBoxStoreState & LabelingBoxStoreActions>(
    (set, get, mutators) => {
      const _temporal = mutators.temporal as StoreApi<
        TemporalState<LabelingBoxStoreState & LabelingBoxStoreActions>
      >;

      const changeBoxProps = <P extends keyof Omit<BoxWithLabel, 'id'>>(
        id: Box['id'],
        propValMap: {
          [K in P]?: Omit<BoxWithLabel, 'id'>[P];
        },
      ) =>
        changeAllBoxProps(
          {
            boxUpdateMap: { [id]: propValMap },
            pcdIndexBoxListMap: get().pcdIndexBoxListMap,
          },
          (pcdIndexBoxListMap, lastModifiedBoxList) => {
            set({
              pcdIndexBoxListMap: { ...pcdIndexBoxListMap },
              lastModifiedBoxList,
            });
          },
        );

      const selectNextOrPrevLabelingBox = (dir: 1 | -1, focus?: boolean) => {
        const boxList = get().getCurrentBoxList();

        if (!boxList.length) return;

        const currentId = Object.keys(get().selectedBoxIdMap)[0];
        const lastSelectedId = get().lastSelectedBoxId;
        const currentIdIndex = boxList.findIndex(box => box.id === currentId);

        if (currentIdIndex === -1 && lastSelectedId) {
          get().selectBox(lastSelectedId, focus);

          return;
        }

        let nextIdIndex = currentIdIndex + dir;

        nextIdIndex =
          dir === 1
            ? nextIdIndex > boxList.length - 1
              ? 0
              : nextIdIndex
            : nextIdIndex < 0
              ? boxList.length - 1
              : nextIdIndex;

        const nextId = boxList[nextIdIndex].id;

        get().selectBox(nextId, focus);
      };

      const setBoxVisible = (id: Box['id'], visible: boolean) => {
        set({
          boxIdAndInvisibleMap: {
            ...get().boxIdAndInvisibleMap,
            [id]: !visible,
          },
        });
      };

      const updateBoxListTransform = <
        P extends Exclude<keyof BoxWithLabel, 'id' | 'label'>,
      >(
        prop: P,
        changeList: [Box['id'], BoxWithLabel[P]][],
      ) => {
        const boxUpdateMap = changeList.reduce(
          (acc, [id, value]) => ({
            ...acc,
            [id]: {
              [prop]: value,
            },
          }),
          {},
        );

        changeAllBoxProps(
          {
            boxUpdateMap,
            pcdIndexBoxListMap: get().pcdIndexBoxListMap,
          },
          (pcdIndexBoxListMap, lastModifiedBoxList) => {
            set({
              pcdIndexBoxListMap: { ...pcdIndexBoxListMap },
              lastModifiedBoxList,
            });
          },
        );
      };

      return {
        boxIdAndInvisibleMap: {},
        focusedBoxId: null,
        lastModifiedBoxList: [],
        lastSelectedBoxId: null,
        pcdIndexBoxListMap: {},
        selectedBoxIdMap: {},
        visibleArrowHelper: false,

        addBox(box) {
          const id = (box as BoxWithLabel).id ?? uuid();
          const boxList = get().getCurrentBoxList();
          const boxRotation = (box as BoxWithLabel).rotation ?? [0, 0, 0];
          const boxWithLabel = {
            ...box,
            id,
            quaternion: eulerToQuaternion(boxRotation),
            rotation: boxRotation,
            position: [box.position[0], box.position[1], box.dimension[2] / 2],
            confidence: 1,
          };

          if (
            Unleash.instance &&
            Unleash.instance.isEnabled('vuex.sapporo.enabledTrackIDManager')
          ) {
            boxWithLabel.trackId = trackIDManager.trackID;
            trackIDManager.next();
          }

          get().setBoxList([...boxList, boxWithLabel as BoxWithLabel]);

          set({
            lastModifiedBoxList: [boxWithLabel as BoxWithLabel],
          });
        },

        changeBoxLabel(id, label) {
          changeBoxProps(id, { label });
        },

        changeBoxTrackID(id, trackId) {
          changeBoxProps(id, { trackId });
        },

        clear() {
          get().clearSelectBoxList();
          set({
            pcdIndexBoxListMap: {},
            lastSelectedBoxId: null,
          });
        },

        clearCurrentBoxList() {
          set({
            pcdIndexBoxListMap: {
              ...get().pcdIndexBoxListMap,
              [getCurrentPcdIndex()]: [],
            },
          });
        },

        clearSelectBoxList() {
          get().selectBoxList([]);
        },

        findBoxByTrackID(trackId) {
          return get()
            .getCurrentBoxList()
            .find(box => box.trackId === trackId);
        },

        focusBox(id) {
          set({ focusedBoxId: id });
        },

        getCurrentBoxList: () =>
          getBoxList({
            pcdIndexBoxListMap: get().pcdIndexBoxListMap,
          }),

        getCurrentBoxIdMap() {
          return get()
            .getCurrentBoxList()
            .reduce(
              (acc, box) => ({
                ...acc,
                [box.id]: true,
              }),
              {},
            );
        },

        getCurrentSelectedBoxList() {
          const selectedBoxIdMap = get().selectedBoxIdMap;
          const boxList = get().getCurrentBoxList();

          return boxList.filter(box => selectedBoxIdMap[box.id]);
        },

        hideBox(id) {
          setBoxVisible(id, false);
        },

        initPcdIndexBoxListMap(frameCount) {
          const pcdIndexBoxListMap = {} as {
            [index: number]: BoxWithLabel[];
          };

          Array.from({ length: frameCount }, (_, i) => {
            pcdIndexBoxListMap[i] = [];
          });

          set({ pcdIndexBoxListMap });
        },

        moveBoxList(changeList) {
          updateBoxListTransform('position', changeList);
        },

        removeBox(id) {
          get().removeBoxList([id]);
        },

        removeBoxList(idList) {
          const pcdIndexBoxListMap = get().pcdIndexBoxListMap;
          const pcdIndex = getCurrentPcdIndex();
          const boxList = get().getCurrentBoxList();
          const newBoxList = boxList.filter(box => {
            const index = idList.findIndex(id => box.id === id);

            return index === -1;
          });

          set({
            pcdIndexBoxListMap: {
              ...pcdIndexBoxListMap,
              [pcdIndex]: [...newBoxList],
            },
            lastModifiedBoxList: newBoxList,
          });

          get().selectBoxList([]);
        },

        resizeBoxList(changeList) {
          updateBoxListTransform('dimension', changeList);
        },

        selectBox(id, focus) {
          get().selectBoxList([id]);

          const box = get()
            .getCurrentBoxList()
            .find(box => box.id === id);

          if (box) {
            useEditingLabelBoxStore.getState().updateTransformedBoxData({
              position: box.position,
              quaternion: box.quaternion ?? eulerToQuaternion(box.rotation),
              dimension: box.dimension,
            });
          }

          if (focus) get().focusBox(id);
        },

        selectBoxList(idList) {
          _temporal.getState().pause();

          const newSelectedBoxIdMap = idList.reduce(
            (acc, id) => ({
              ...acc,
              [id]: true,
            }),
            {},
          );

          if (shallow(get().selectedBoxIdMap, newSelectedBoxIdMap)) return;

          set({
            selectedBoxIdMap: newSelectedBoxIdMap,
          });

          idList.length &&
            set({ lastSelectedBoxId: idList[idList.length - 1] });

          _temporal.getState().resume();
        },

        selectNextBox: focus => selectNextOrPrevLabelingBox(1, focus),

        selectPrevBox: focus => selectNextOrPrevLabelingBox(-1, focus),

        setBoxList(boxList, index) {
          const pcdIndexBoxListMap = get().pcdIndexBoxListMap;
          const pcdIndex = index ?? getCurrentPcdIndex();

          set({
            pcdIndexBoxListMap: {
              ...pcdIndexBoxListMap,
              [pcdIndex]: boxList?.map(box => {
                let trackId = box.trackId;

                if (
                  Unleash.instance &&
                  Unleash.instance.isEnabled(
                    'vuex.sapporo.enabledTrackIDManager',
                  ) &&
                  !TrackIDManager.isValidTrackID(box.trackId)
                ) {
                  trackId = trackIDManager.trackID;
                  trackIDManager.next();
                }

                return { ...box, trackId };
              }),
            },
            lastModifiedBoxList: boxList as BoxWithLabel[],
          });
        },

        showBox(id) {
          setBoxVisible(id, true);
        },

        setVisibleArrowHelper(visibleArrowHelper) {
          set({ visibleArrowHelper });
        },

        toggleVisibleArrowHelper() {
          get().setVisibleArrowHelper(!get().visibleArrowHelper);
        },

        updateQuaternion(changeList) {
          updateBoxListTransform('quaternion', changeList);
          updateBoxListTransform(
            'rotation',
            changeList.map(([id, quaternion]) => [
              id,
              quaternionToEuler(quaternion).slice(0, 3) as Vector3Tuple,
            ]),
          );
        },
      };
    },
    {
      equality(pastState, currentState) {
        return (
          isEqual(
            pastState.boxIdAndInvisibleMap,
            currentState.boxIdAndInvisibleMap,
          ) &&
          isEqual(
            pastState.lastSelectedBoxId,
            currentState.lastSelectedBoxId,
          ) &&
          isEqual(
            pastState.pcdIndexBoxListMap,
            currentState.pcdIndexBoxListMap,
          ) &&
          isEqual(pastState.selectedBoxIdMap, currentState.selectedBoxIdMap)
        );
      },
      handleSet: handleSet => throttle<typeof handleSet>(handleSet, 100),
    },
  ),
  shallow,
);

export default useLabelingBoxStore;
